import { CustomerContractTypeModel } from '../shared/models/customer-contract-type-model';
import { CustomerService } from '../shared/services/customer.service';
import { ValidationUtils } from '../../shared/validation/validation-runner';
import { IValidator, Validator } from '../../shared/validation/validators';
import { FieldState } from '../../shared/models/field-state';
import { UrlConfigContainer } from '../../core/url-config';
import { UrlConfigService } from '@hrra/core';
import {
  UploadOutput,
  UploadInput,
  UploaderOptions,
} from '@angular-ex/uploader';
import { CommonLookupItem } from '../../shared/models/common-lookup-item.model';
import { CustomerContractModel } from '../shared/models/customer-contract-model';
import { CustomerModel } from '../shared/models/customer-model';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnInit,
  Output,
} from '@angular/core';
import { CustomMessageService } from '@admin/core/message.service';
import { ApplicationMessage } from '@admin/core/models/application-message';
import { MessageType } from '@admin/core/models/enum/message-type';
import { ApplicationMessageSource } from '@admin/core/models/application-message-source';
import { ConfigurationService } from '@admin/shared/services/configuration.service';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'hr-customer-edit-contracts',
  templateUrl: './customer-edit-contracts.component.html',
  styleUrls: ['./customer-edit-contracts.component.less'],
  standalone: false,
})
export class CustomerEditContractsComponent implements OnInit {
  @Output()
  public onUpdate: EventEmitter<CustomerContractModel> =
    new EventEmitter<CustomerContractModel>();

  @Input('customerContracts')
  public customerContracts: CustomerContractModel[];

  @Input('contractTypes')
  public contractTypes: CommonLookupItem<number, string>[];

  @Input('customerId')
  public customerId: number;

  public isInAddMode: Boolean = false;

  public newContract: CustomerContractModel;
  public attachmentMaxSize: number;
  public attachmentUploadErrorOccured: boolean;

  uploadOptions: UploaderOptions;
  uploadInput: EventEmitter<UploadInput>;

  public hasOldContracts: Boolean = true;

  public fieldStates: { expireDate: FieldState; type: FieldState };

  public get field() {
    return this.fieldStates;
  }

  constructor(
    @Inject(NgZone) private zone: NgZone,
    private urlService: UrlConfigService<UrlConfigContainer>,
    private router: Router,
    private route: ActivatedRoute,
    private translationService: TranslateService,
    private config: ConfigurationService,
    private customerService: CustomerService,
    private messageService: CustomMessageService
  ) {
    this.customerContracts = [];
    this.newContract = new CustomerContractModel();
    this.attachmentUploadErrorOccured = false;
    this.uploadOptions = { concurrency: 1, maxUploads: 1 };
    this.uploadInput = new EventEmitter<UploadInput>();
  }

  public initializeFieldsState() {
    this.fieldStates = {
      expireDate: new FieldState(
        () => this.newContract && this.newContract.expireDate
      ),
      type: new FieldState(() => this.newContract && this.newContract.type),
    };
  }

  public invokeValidation(validators: IValidator[], onValid?: () => void) {
    ValidationUtils.invokeValidation(
      validators,
      this.getFieldStatesObject(),
      onValid
    );
  }

  public onAddClick() {
    this.attachmentUploadErrorOccured = false;
    this.isInAddMode = true;
  }

  public onDelete(id: number) {
    if (confirm(this.translationService.instant('message.AreYouSure'))) {
      this.customerService.deleteCustomerContract(id).subscribe(() => {
        // because angular's same route navigation (reload) is ugly
        if (window.location.href.includes('/contracts')) {
          window.location.reload();
        } else {
          this.router.navigate([
            '/customers/edit/',
            this.customerId,
            'contracts',
          ]);
        }
      });
    }
  }

  public onCancelClick() {
    this.isInAddMode = false;
    this.newContract = new CustomerContractModel();
  }

  public onSaveClick() {
    let contract = this.newContract;
    contract.customer = this.customerId;
    let varlidators = this.getValidators();
    this.invokeValidation(varlidators, () => {
      this.customerService.saveCustomerContract(contract).subscribe(() => {
        this.initializeFieldsState();
        this.isInAddMode = false;
        this.newContract = new CustomerContractModel();
        this.onUpdate.emit(contract);
      });
    });
  }

  onUploadOutput(output: UploadOutput): void {
    if (output.type === 'allAddedToQueue') {
      const event: UploadInput = {
        type: 'uploadAll',
        url: this.urlService.urlConfig.api.data.file.upload,
        method: 'POST',
      };
      this.uploadInput.emit(event);
    } else if (output.type === 'done' && output.file.responseStatus === 200) {
      this.newContract.fileName = output.file.response.fileName;
    } else if (output.type === 'done' && output.file.responseStatus === 500) {
      this.attachmentUploadErrorOccured = true;
      this.messageService.pushMessage(<ApplicationMessage>{
        type: MessageType.Error,
        sourceId: ApplicationMessageSource.global,
        summary: '',
        detail: 'file must be max ' + this.attachmentMaxSize + ' MB',
      });
    }
  }

  private getValidators() {
    let field = this.field;
    let contract = this.newContract;

    let emptyMessage = this.translationService.instant(
      'message.ValueLeftEmpty'
    );

    return [
      Validator.forRequired(
        field.expireDate,
        () => contract && contract.expireDate,
        emptyMessage
      ),
      Validator.forRequired(
        field.type,
        () => contract && contract.type && contract.type.customerContractTypeId,
        emptyMessage
      ),
    ];
  }

  private getFieldStatesObject(): { [index: string]: FieldState } {
    let result: { [index: string]: FieldState } = {};
    for (let p in this.fieldStates) {
      if (this.fieldStates.hasOwnProperty(p)) {
        result[this.fieldStates[p].fieldId] = this.fieldStates[p];
      }
    }

    return result;
  }

  ngOnInit() {
    const config = this.config.getCachedConfig();
    this.attachmentMaxSize =
      config.customerContractAttachmentMaxSize / 1024 / 1024;

    this.initializeFieldsState();
    this.hasOldContracts =
      this.customerContracts &&
      this.customerContracts.some((s) => s.isActive == false);
  }
}
